import { useEffect, type RefObject } from 'react';

/**
 * Closes the popover when focus leaves it, except when focus moves to the
 * anchor/trigger. This avoids racing with the trigger's click handler when the
 * user clicks the trigger to toggle the menu closed (focusout would otherwise
 * fire `onClose`, then the click handler would toggle the menu back open).
 */
export function useMenuCloseOnBlur({
    enabled,
    popoverRef,
    anchorEl,
    onClose,
}: {
    enabled: boolean;
    popoverRef: RefObject<HTMLElement | null>;
    anchorEl: HTMLElement | null;
    onClose: () => void;
}): void {
    useEffect(() => {
        if (!enabled) {
            return undefined;
        }

        const pop = popoverRef.current;

        if (!pop) {
            return undefined;
        }

        const handleFocusOut = (e: FocusEvent): void => {
            const next = e.relatedTarget;
            if (next instanceof Node) {
                if (pop.contains(next)) {
                    return;
                }

                if (anchorEl && (anchorEl === next || anchorEl.contains(next))) {
                    return;
                }
            }
            onClose();
        };

        pop.addEventListener('focusout', handleFocusOut);

        return () => {
            pop.removeEventListener('focusout', handleFocusOut);
        };
    }, [enabled, popoverRef, anchorEl, onClose]);
}
